import React from 'react';
import { useChanges, Form } from '../../../../../../components/Form/Form';
import { addProductRecall, updateProductRecall, EventRecallDetails } from '../../../../../../model/event';
import { ModalController } from '../../../../../../components/Modal/ControlledModal';
import { t } from '../../../../../../locales/translate';
import { handleError } from '../../../../../../model/notification';
import { Switcher } from '../../../../../../components/Form/Switcher/Switcher';
import { TextField } from '../../../../../../components/Form/TextField/TextField';
import { TextArea } from '../../../../../../components/Form/TextArea/TextArea';
import { Stack } from '../../../../../../components/Stack/Stack';

interface Props {
  controller: ModalController<EventRecallDetails>;
}

export function MassRefundForm({ controller }: Props): JSX.Element {
  const [, , recallDetails, trackChanges] = useChanges<EventRecallDetails>(controller.model);
  const massRefundLabels = [t.general.yes.toLocaleUpperCase(), t.general.no.toLocaleUpperCase()];

  async function handleOnSubmit(): Promise<void> {
    try {
      if (controller.model.id) {
        await updateProductRecall(recallDetails);
      } else {
        await addProductRecall(recallDetails);
      }
      controller.close();
    } catch (error) {
      handleError(error);
    }
  }

  return (
    <Form model={recallDetails} onInput={trackChanges}>
      {(form) => {
        controller.configure({
          onSubmit: handleOnSubmit,
          canSubmit: form.valid,
        });
        return (
          <Stack>
            <Switcher
              value={recallDetails.paymentsMassBookable}
              label={t.model.event.massRefund.autoBookRefunds}
              labels={massRefundLabels}
              id="test-mass-refund-switcher"
              onChange={(value) => {
                trackChanges({ paymentsMassBookable: value }, 'paymentsMassBookable');
              }}
            />
            <Switcher
              value={recallDetails.usedTicketsMassRefundable}
              label={t.model.event.massRefund.usedTicketsMassRefundable}
              labels={massRefundLabels}
              id="used-tickets-mass-refund-switcher"
              onChange={(value) => {
                trackChanges({ usedTicketsMassRefundable: value }, 'usedTicketsMassRefundable');
              }}
            />
            <Switcher
              value={recallDetails.paused}
              label={t.model.event.massRefund.pauseMassRefund}
              labels={massRefundLabels}
              id="pause-mass-refund-switcher"
              onChange={(value) => {
                trackChanges({ paused: value }, 'paused');
              }}
            />
            <TextField label={t.model.event.massRefund.emailSubjectDe} name="emailSubjectDe" />
            <TextArea label={t.model.event.massRefund.emailTemplateDe} name="emailTemplateDe" />
            <TextField label={t.model.event.massRefund.emailSubjectEn} name="emailSubjectEn" />
            <TextArea label={t.model.event.massRefund.emailTemplateEn} name="emailTemplateEn" />
            <TextField label={t.model.event.massRefund.customTitleDe} name="titleDe" />
            <TextField label={t.model.event.massRefund.customTitleEn} name="titleEn" />
            <TextArea label={t.model.event.massRefund.refundExplanationDe} name="textDe" />
            <TextArea label={t.model.event.massRefund.refundExplanationEn} name="textEn" />
          </Stack>
        );
      }}
    </Form>
  );
}
